"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { usePersona } from "./persona-context";
import type { AnalyticsConfig } from "@/lib/analytics-schema";
import { cancelAnalyticsNavigation, clearAnalyticsIdentity, trackAnalyticsPage, trackFeatureExposure } from "@/lib/product-analytics";

export function ProductAnalytics({ config }: { config: AnalyticsConfig | null }) {
  const pathname = usePathname();
  const { persona } = usePersona();
  useEffect(() => {
    let disposed = false;
    let observer: IntersectionObserver | undefined;
    const sync = () => {
      if (config && document.visibilityState !== "visible") return;
      void trackAnalyticsPage(config, pathname, persona.role).then(() => {
        if (disposed || !config || typeof IntersectionObserver === "undefined") return;
        observer?.disconnect();
        observer = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.5 || document.visibilityState !== "visible") continue;
            const feature = (entry.target as HTMLElement).dataset.analyticsFeature;
            if (feature) trackFeatureExposure(feature);
          }
        }, { threshold: 0.5 });
        document.querySelectorAll("[data-analytics-feature]").forEach((node) => observer!.observe(node));
      });
    };
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", sync);
      observer?.disconnect(); cancelAnalyticsNavigation();
    };
  }, [config, pathname, persona.role]);
  return null;
}

export function ClearAnalyticsIdentity() {
  useEffect(() => { clearAnalyticsIdentity(); }, []);
  return null;
}
